import { useEffect } from 'react';

const PLAY =
  '<svg width="10" height="11" viewBox="0 0 10 11" fill="currentColor" aria-hidden="true"><path d="M0 0l10 5.5L0 11z"/></svg>';
const PAUSE =
  '<svg width="10" height="11" viewBox="0 0 10 11" fill="currentColor" aria-hidden="true"><rect x="0" y="0" width="3.4" height="11"/><rect x="6.6" y="0" width="3.4" height="11"/></svg>';

function fmt(s: number) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
}

/**
 * Citation popovers (site.js module 5). Every `<button class="cite" data-quote=...>`
 * chip opens a single shared popover that is re-parented into the chip's `.cites`
 * row, so its position is pure layout. Ported as an imperative effect because the
 * chips live inside large static mock-ups; re-runs on every route change.
 *
 * Chip attributes: data-who, data-quote, data-dur ("mm:ss"), data-agent="1".
 */
export function useCitePopovers(key: string) {
  useEffect(() => {
    const chips = Array.from(document.querySelectorAll<HTMLButtonElement>('.cite[data-quote]'));
    if (!chips.length) return;

    const pop = document.createElement('div');
    pop.className = 'cite-pop on-ink';
    pop.id = 'citePop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-label', 'Verbatim clip');
    pop.setAttribute('data-open', 'false');
    pop.innerHTML =
      '<p class="who"><span id="cpWho"></span> <span class="verified" id="cpBadge">' +
      '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg> <span id="cpBadgeT">participant</span></span></p>' +
      '<blockquote id="cpQuote"></blockquote>' +
      '<div class="player">' +
      '<button type="button" class="play" id="cpPlay" aria-label="Play clip">' +
      PLAY +
      '</button>' +
      '<span class="scrub"><span class="scrub-bar"><i id="cpBar"></i></span>' +
      '<span class="scrub-t"><span id="cpNow">00:00</span><span id="cpDur">00:00</span></span></span>' +
      '</div>';

    const q = <T extends Element>(s: string) => pop.querySelector<T>(s)!;
    const cpWho = q<HTMLSpanElement>('#cpWho');
    const cpQuote = q<HTMLElement>('#cpQuote');
    const cpPlay = q<HTMLButtonElement>('#cpPlay');
    const cpBar = q<HTMLElement>('#cpBar');
    const cpNow = q<HTMLSpanElement>('#cpNow');
    const cpDur = q<HTMLSpanElement>('#cpDur');
    const cpBadge = q<HTMLSpanElement>('#cpBadge');
    const cpBadgeT = q<HTMLSpanElement>('#cpBadgeT');

    let current: HTMLButtonElement | null = null;
    let timer: number | null = null;
    let pos = 0;
    let total = 30;

    function stopPlay() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      cpPlay.innerHTML = PLAY;
      cpPlay.setAttribute('aria-label', 'Play clip');
    }
    function place(chip: HTMLElement) {
      const row = chip.parentNode as HTMLElement;
      if (pop.parentNode !== row) row.appendChild(pop);
      pop.classList.remove('flip');
      pop.style.left = '0px';
      const pw = pop.offsetWidth;
      const rowW = row.clientWidth;
      let left = chip.offsetLeft;
      const maxL = rowW - pw;
      if (left > maxL) left = maxL;
      if (left < 0) left = 0;
      const rowLeft = row.getBoundingClientRect().left;
      const vw = document.documentElement.clientWidth;
      if (rowLeft + left + pw > vw - 10) left = Math.max(0, vw - 10 - pw - rowLeft);
      pop.style.left = Math.round(left) + 'px';
      const ax = Math.min(Math.max(chip.offsetLeft - left + 9, 10), pw - 20);
      pop.style.setProperty('--ax', Math.round(ax) + 'px');
      const r = chip.getBoundingClientRect();
      const ph = pop.offsetHeight;
      if (r.bottom + 9 + ph > window.innerHeight - 8 && r.top - ph - 9 > 8) pop.classList.add('flip');
    }
    function close() {
      if (!current) return;
      current.setAttribute('aria-expanded', 'false');
      current = null;
      pop.setAttribute('data-open', 'false');
      stopPlay();
    }
    function open(chip: HTMLButtonElement) {
      if (current === chip) {
        close();
        return;
      }
      if (current) current.setAttribute('aria-expanded', 'false');
      current = chip;
      chip.setAttribute('aria-expanded', 'true');
      cpWho.textContent = chip.getAttribute('data-who') || 'Participant';
      // An agent clip is never labelled "participant"; it needs a human to confirm it.
      const isAgent = chip.getAttribute('data-agent') === '1';
      cpBadge.classList.toggle('unconfirmed', isAgent);
      cpBadgeT.textContent = isAgent ? 'needs human confirmation' : 'participant';
      cpQuote.textContent = '“' + (chip.getAttribute('data-quote') || '') + '”';
      const d = (chip.getAttribute('data-dur') || '00:30').split(':');
      total = (parseInt(d[0], 10) || 0) * 60 + (parseInt(d[1], 10) || 30);
      pos = 0;
      cpBar.style.width = '0%';
      cpNow.textContent = '00:00';
      cpDur.textContent = chip.getAttribute('data-dur') || '00:30';
      stopPlay();
      pop.setAttribute('data-open', 'true');
      place(chip);
    }

    const onChipClick = (e: Event) => {
      e.stopPropagation();
      open(e.currentTarget as HTMLButtonElement);
    };
    const onChipEnter = (e: Event) => {
      if (!current) open(e.currentTarget as HTMLButtonElement);
    };
    chips.forEach((chip) => {
      chip.addEventListener('click', onChipClick);
      chip.addEventListener('mouseenter', onChipEnter);
    });

    const onPlay = (e: Event) => {
      e.stopPropagation();
      if (timer) {
        stopPlay();
        return;
      }
      cpPlay.innerHTML = PAUSE;
      cpPlay.setAttribute('aria-label', 'Pause clip');
      timer = window.setInterval(() => {
        pos += 0.25;
        if (pos >= total) {
          pos = total;
          cpBar.style.width = '100%';
          cpNow.textContent = fmt(total);
          stopPlay();
          return;
        }
        cpBar.style.width = ((pos / total) * 100).toFixed(1) + '%';
        cpNow.textContent = fmt(pos);
      }, 250);
    };
    cpPlay.addEventListener('click', onPlay);
    const stopProp = (e: Event) => e.stopPropagation();
    pop.addEventListener('click', stopProp);
    document.addEventListener('click', close);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && current) {
        const c = current;
        close();
        c.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    let rz: number | undefined;
    const onResize = () => {
      if (!current) return;
      clearTimeout(rz);
      rz = window.setTimeout(() => {
        if (current) place(current);
      }, 180);
    };
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      stopPlay();
      chips.forEach((chip) => {
        chip.removeEventListener('click', onChipClick);
        chip.removeEventListener('mouseenter', onChipEnter);
      });
      cpPlay.removeEventListener('click', onPlay);
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      clearTimeout(rz);
      pop.remove();
    };
  }, [key]);
}
